import React from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import {
  LayoutDashboard, CalendarClock, Package as PackageIcon, Users, FileText,
  Wallet, Newspaper, Settings, LogOut, Menu, X, Bell, ChevronRight,
  User as UserIcon, Send, Calendar as CalendarIcon, Star,
} from "lucide-react";

const NAV_ITEMS = [
  { to: "/admin",            label: "Dashboard",        icon: LayoutDashboard, end: true },
  { to: "/admin/bookings",   label: "Bookings",         icon: CalendarClock },
  { to: "/admin/paket",      label: "Kelola Paket",     icon: PackageIcon },
  { to: "/admin/jamaah",     label: "Kelola Jamaah",    icon: Users },
  { to: "/admin/dokumen",    label: "Verifikasi Dok",   icon: FileText },
  { to: "/admin/pembayaran", label: "Pembayaran",       icon: Wallet },
  { to: "/admin/manasik",    label: "Kelola Manasik",   icon: CalendarIcon },
  { to: "/admin/alumni",     label: "Alumni Testimoni", icon: Star },
  { to: "/admin/broadcast",  label: "Broadcast WA",     icon: Send },
  { to: "/admin/konten",     label: "Konten & Blog",    icon: Newspaper },
  { to: "/admin/ustadz",     label: "Kelola Ustadz",    icon: UserIcon },
  { to: "/admin/messages",   label: "Pesan Kontak",     icon: Bell },
  { to: "/admin/pengaturan", label: "Kelola Staf",      icon: Settings, hint: "Superadmin" },
];

function SidebarLink({ item, onClick }) {
  const Icon = item.icon;
  const cls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors group ${
      item.disabled
        ? "text-white/30 cursor-not-allowed"
        : isActive
        ? "bg-accent text-primary shadow-sm"
        : "text-white/75 hover:bg-white/10 hover:text-white"
    }`;
  if (item.disabled) {
    return (
      <div className={cls({ isActive: false })} title={item.hint} data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1">{item.label}</span>
        {item.hint && <span className="text-[9px] uppercase tracking-widest text-white/40">{item.hint}</span>}
      </div>
    );
  }
  return (
    <NavLink to={item.to} end={item.end} className={cls} onClick={onClick} data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon className="h-4 w-4 shrink-0" />
      <span className="flex-1">{item.label}</span>
      <ChevronRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-60" />
    </NavLink>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const nav = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  React.useEffect(() => { setSidebarOpen(false); }, [pathname]);

  const currentPage = NAV_ITEMS.find((n) => n.end ? pathname === n.to : pathname.startsWith(n.to));

  return (
    <div className="min-h-screen bg-muted/30 flex" data-testid="admin-layout">
      {/* Sidebar — desktop static, mobile drawer */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-72 bg-primary text-white flex flex-col transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div className="bg-white/95 rounded-md px-3 py-2 shadow-md">
            <BrandLogo className="h-8" />
          </div>
          <button
            className="lg:hidden text-white/70 hover:text-white"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
            data-testid="admin-sidebar-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-1" data-testid="admin-nav">
          <p className="text-[10px] uppercase tracking-[0.24em] text-white/40 px-4 py-2">Menu</p>
          {NAV_ITEMS.map((item) => (
            <SidebarLink key={item.to} item={item} onClick={() => setSidebarOpen(false)} />
          ))}
        </nav>

        {/* Sidebar footer — user + logout */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="h-9 w-9 rounded-full bg-accent text-primary grid place-items-center font-semibold text-sm">
              {(user?.name || "A").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{user?.name || "Admin"}</p>
              <p className="text-[10px] text-white/50 truncate">{user?.email || "-"}</p>
            </div>
          </div>
          <button
            onClick={async () => { await logout(); nav("/", { replace: true }); }}
            data-testid="admin-logout"
            className="w-full inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/75 hover:bg-white/10 hover:text-white transition-colors"
          >
            <LogOut className="h-4 w-4" /> Keluar
          </button>
        </div>
      </aside>

      {/* Drawer backdrop (mobile) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-border px-6 py-4 flex items-center justify-between" data-testid="admin-topbar">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="lg:hidden text-primary hover:opacity-80"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open sidebar"
              data-testid="admin-sidebar-open"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Panel Admin</p>
              <h1 className="font-serif text-lg lg:text-xl text-primary truncate leading-tight" style={{ fontSize: "clamp(1.05rem, 1.5vw + 0.5rem, 1.35rem)" }}>
                {currentPage?.label || "Admin"}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="relative p-2 rounded-full hover:bg-muted transition-colors"
              aria-label="Notifications"
              data-testid="admin-notif-btn"
              title="Notifikasi (segera)"
            >
              <Bell className="h-4 w-4 text-primary" />
            </button>
            <a href="/" className="hidden sm:inline-flex text-xs text-muted-foreground hover:text-primary underline-offset-4 hover:underline" data-testid="admin-view-site">
              Lihat Situs Publik
            </a>
          </div>
        </header>

        {/* Content area */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0" data-testid="admin-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
